<script setup lang="ts">
import { computed, ref } from 'vue';
import type {
  AffiliateConfig,
  BannerItem,
  GameSectionConfig,
  GameItem,
} from '~/utils/perfilOficialConfigs';
import { usePerfilOficialSections } from '~/composables/apostatudo/use-perfil-oficial-sections';
import { fetchPerfilOficialBannersHandler } from '~/handlers/fetch-perfil-oficial-banners.handler';

const props = defineProps<{
  config: AffiliateConfig;
  isRoletasPage: boolean;
  promocode?: string;
  perfilSlug?: string;
}>();

const accentColor = computed(() => props.config.accentColor);

const accentCssVars = computed(() => {
  if (!accentColor.value) return {};

  return {
    '--accent-color': accentColor.value,
  } as Record<string, string>;
});

// ============================================================================
// API INTEGRATION - Busca seções de jogos via API (como a HomeV2)
// ============================================================================

const {
  fetchSections,
  validItems: apiItems,
  hasItems: hasApiItems,
  loading: isLoadingSections,
} = usePerfilOficialSections();

// ============================================================================
// API BANNERS - Busca banners por slug, fallback para config
// ============================================================================

const apiBanners = ref<BannerItem[]>([]);

const activeBanners = computed(() =>
  apiBanners.value.length > 0 ? apiBanners.value : props.config.banner,
);

async function fetchBannersForPerfil(slug: string) {
  const { banners } = await fetchPerfilOficialBannersHandler({ slug });

  apiBanners.value = banners;
}

// Buscar dados da API ao montar e ao trocar de perfil
onMounted(async () => {
  if (props.perfilSlug) {
    await Promise.all([
      fetchSections(props.perfilSlug),
      fetchBannersForPerfil(props.perfilSlug),
    ]);
  }
});

watch(
  () => props.perfilSlug,
  async (newSlug) => {
    if (newSlug) {
      apiBanners.value = [];
      await Promise.all([
        fetchSections(newSlug),
        fetchBannersForPerfil(newSlug),
      ]);
    }
  },
);

// ============================================================================
// MAPEAMENTO: PLACEHOLDER slugs → tipos de seção especial
// ============================================================================

const PLACEHOLDER_PREFIX_MAP: [RegExp, string][] = [
  [/^(miss[oõa]es|miss[aã]o|missions?)([-_]|$)/, 'missions'],
  [/^(torneios?|tournaments?)([-_]|$)/, 'tournaments'],
  [/^(minigames?|mini[-_]games?)([-_]|$)/, 'minigames'],
  [/^(vip|area[-_]?vip)([-_]|$)/, 'vip'],
  [/^(album[-_]?copa|album)([-_]|$)/, 'album'],
  [
    /(jogos[-_]?em[-_]?destaque|jogos[-_]?destaque|destaque[-_]?esport)/,
    'curated-events',
  ],
  [/^tabs$/, 'tabs'],
];

function getPlaceholderType(slug: string): string | null {
  const normalized = slug.toLowerCase();

  for (const [pattern, type] of PLACEHOLDER_PREFIX_MAP) {
    if (pattern.test(normalized)) return type;
  }

  return null;
}

// ============================================================================
// MAPEAMENTO: HomeGame[] → GameItem[]
// ============================================================================

function normalizeSlug(slug: string | null | undefined): string {
  return slug?.toLowerCase() || '';
}

function normalizeImageUrl(url: string): string {
  if (!url) return url;

  return url.replace(/\/(public|mobile|ipad)$/, '');
}

function mapGamesToPerfilOficialFormat(games: HomeGame[]): GameItem[] {
  return games
    .sort((a, b) => a.pivot.priority - b.pivot.priority)
    .map((game) => ({
      id: String(game.id),
      name: game.name,
      slug: normalizeSlug(game.slug),
      image: normalizeImageUrl(game.image),
      pay_today: game.pay_today ?? null,
      ribbon: game.ribbon ?? null,
      tags: game.tags ?? null,
      provider: game.provider
        ? { id: game.provider.id, name: game.provider.name }
        : null,
    }));
}

// Type guard para HomeTabsData (tem categories)
function isHomeTabsData(
  data: HomeCategoryData | HomeTabsData,
): data is HomeTabsData {
  return 'categories' in data;
}

// Mapeia categories da API para o formato de tabs do SectionsTabsApostatudo
function mapCategoriesToTabs(categories: HomeTabCategory[]) {
  return categories.map((category) => ({
    title: category.name,
    slug: category.slug?.toLowerCase() || undefined,
    icon: category.item_config?.icon || undefined,
    see_more_url: category.item_config?.see_more_url || null,
    child: category.games
      .sort((a, b) => a.pivot.priority - b.pivot.priority)
      .map((game) => ({
        id: String(game.id),
        name: game.name,
        slug: normalizeSlug(game.slug),
        image: normalizeImageUrl(game.image),
        pay_today: game.pay_today ?? null,
        ribbon: game.ribbon ?? null,
        tags: game.tags ?? null,
        provider: game.provider
          ? { id: game.provider.id, name: game.provider.name }
          : null,
      })),
  }));
}

// ============================================================================
// HELPERS: Criação de seções
// ============================================================================

// Helper function to create mission section
const createMissionSection = (
  config: AffiliateConfig,
  missionSectionCount: number,
) => {
  const missionFilters = config.smarticoMissionFilter;
  let filtersForThisMission: string[] = [];

  if (
    config.splitMissionFilters &&
    Array.isArray(missionFilters) &&
    missionFilters.length >= 2
  ) {
    if (missionSectionCount === 1 && missionFilters[0]) {
      filtersForThisMission = [missionFilters[0]];
    } else if (missionSectionCount === 2 && missionFilters[1]) {
      filtersForThisMission = [missionFilters[1]];
    } else {
      filtersForThisMission = missionFilters;
    }
  } else {
    filtersForThisMission = missionFilters;
  }

  const specificConfig = {
    ...config,
    smarticoMissionFilter: filtersForThisMission,
    originalSmarticoMissionFilter: config.smarticoMissionFilter,
  };

  return {
    type: 'missions' as const,
    data: { config: specificConfig },
  };
};

// Helper function to create game list section from config (fallback)
const createGameListSectionFromConfig = (
  config: AffiliateConfig,
  sectionType: string,
) => {
  const gameSectionConfig = config.gameSections.find(
    (gs: GameSectionConfig) => gs.key === sectionType,
  );

  if (!gameSectionConfig) return null;

  return {
    type: 'gameList' as const,
    data: {
      title: gameSectionConfig.title,
      iconPath: gameSectionConfig.iconPath,
      items: gameSectionConfig.games,
      seeMoreUrl: gameSectionConfig.seeMoreUrl || '',
      showNumbers: gameSectionConfig.showNumbers,
    },
  };
};

// Helper function to create game list section from API item
const createGameListSectionFromApi = (apiItem: HomeSectionItem) => {
  if (!('games' in apiItem.data) || apiItem.data.games.length === 0) {
    return null;
  }

  return {
    type: 'gameList' as const,
    data: {
      title: apiItem.data.name,
      iconPath: apiItem.icon || 'home/slots',
      items: mapGamesToPerfilOficialFormat(apiItem.data.games),
      seeMoreUrl: apiItem.see_more_url || '',
      showNumbers: apiItem.content_type === 'NUMBER',
    },
  };
};

// ============================================================================
// COMPUTED: Seções da página
// ============================================================================

type TabData = {
  title: string;
  slug?: string;
  icon?: string;
  see_more_url?: string | null;
  child: GameItem[];
};

type PageSection =
  | {
      type: 'banner';
      data: {
        banner: AffiliateConfig['banner'];
        config: AffiliateConfig;
        affiliateDetails: AffiliateConfig['affiliateDetails'];
        promocode?: string;
      };
    }
  | { type: 'tabs' }
  | { type: 'missions'; data: { config: AffiliateConfig } }
  | { type: 'tournaments'; data: { config: AffiliateConfig } }
  | { type: 'minigames'; data: { config: AffiliateConfig } }
  | { type: 'vip'; data: { config: AffiliateConfig } }
  | { type: 'album'; data: { config: AffiliateConfig } }
  | { type: 'curated-events' }
  | {
      type: 'gameList';
      data: {
        title: string;
        iconPath: string;
        items: GameItem[];
        seeMoreUrl: string;
        key?: string;
        showNumbers?: boolean;
      };
    }
  | {
      type: 'apiTabs';
      data: {
        title: string;
        iconPath: string;
        seeMoreUrl?: string;
        tabs: TabData[];
        isTabVisible: boolean;
      };
    };

const pageSections = computed(() => {
  const sections: PageSection[] = [];

  // Quando a API retorna dados, a API controla TUDO (exceto banner)
  if (hasApiItems.value) {
    // Banner: usa API se disponível, senão fallback do config
    sections.push({
      type: 'banner',
      data: {
        banner: activeBanners.value,
        config: props.config,
        affiliateDetails: props.config.affiliateDetails,
        promocode: props.promocode,
      },
    });

    // Processa items da API por ordem de prioridade
    let missionSectionCount = 0;

    for (const apiItem of apiItems.value) {
      // PLACEHOLDER → componente especial (missions, tournaments, minigames, tabs)
      if (apiItem.content_type === 'PLACEHOLDER') {
        const placeholderType = getPlaceholderType(apiItem.data.slug);

        if (placeholderType === 'missions') {
          missionSectionCount++;
          sections.push(
            createMissionSection(props.config, missionSectionCount),
          );
        } else if (placeholderType === 'tournaments') {
          sections.push({
            type: 'tournaments',
            data: { config: props.config },
          });
        } else if (placeholderType === 'minigames') {
          sections.push({
            type: 'minigames',
            data: { config: props.config },
          });
        } else if (placeholderType === 'vip') {
          sections.push({
            type: 'vip',
            data: { config: props.config },
          });
        } else if (placeholderType === 'album') {
          sections.push({
            type: 'album',
            data: { config: props.config },
          });
        } else if (placeholderType === 'curated-events') {
          sections.push({ type: 'curated-events' });
        } else if (placeholderType === 'tabs') {
          sections.push({ type: 'tabs' });
        }

        continue;
      }

      // TABS → swiper com abas (ex: saraadm com Tada, PgSoft, Pragmatic, Popok)
      if (apiItem.content_type === 'TABS') {
        if (
          isHomeTabsData(apiItem.data) &&
          apiItem.data.categories.length > 0
        ) {
          const tabs = mapCategoriesToTabs(apiItem.data.categories);

          sections.push({
            type: 'apiTabs',
            data: {
              title: apiItem.data.name,
              iconPath: apiItem.icon || 'home/slots',
              seeMoreUrl: apiItem.see_more_url || undefined,
              tabs,
              isTabVisible: apiItem.data.categories.length > 1,
            },
          });
        }

        continue;
      }

      // NUMBER / BASIC → lista de jogos
      const gameListSection = createGameListSectionFromApi(apiItem);

      if (gameListSection) {
        sections.push(gameListSection);
      }
    }

    return sections;
  }

  // ============================================================================
  // FALLBACK: Sem dados da API → usar pageSectionOrder do config (hardcoded)
  // Enquanto a API está carregando, NÃO mostrar fallback (evita flash)
  // ============================================================================

  if (props.perfilSlug && isLoadingSections.value) {
    return sections;
  }

  let missionSectionCount = 0;

  for (const sectionType of props.config.pageSectionOrder) {
    if (sectionType === 'banner') {
      sections.push({
        type: 'banner',
        data: {
          banner: activeBanners.value,
          config: props.config,
          affiliateDetails: props.config.affiliateDetails,
          promocode: props.promocode,
        },
      });
    } else if (sectionType === 'tabs') {
      sections.push({ type: 'tabs' });
    } else if (sectionType === 'missions') {
      missionSectionCount++;
      sections.push(createMissionSection(props.config, missionSectionCount));
    } else if (sectionType === 'tournaments') {
      sections.push({ type: 'tournaments', data: { config: props.config } });
    } else if (sectionType === 'minigames') {
      sections.push({ type: 'minigames', data: { config: props.config } });
    } else if (sectionType === 'vip') {
      sections.push({ type: 'vip', data: { config: props.config } });
    } else if (getPlaceholderType(sectionType) === 'album') {
      sections.push({ type: 'album', data: { config: props.config } });
    } else if (getPlaceholderType(sectionType) === 'curated-events') {
      sections.push({ type: 'curated-events' });
    } else {
      const gameListSection = createGameListSectionFromConfig(
        props.config,
        sectionType,
      );

      if (gameListSection) {
        sections.push(gameListSection);
      }
    }
  }

  return sections;
});

const hasCuratedEventsSection = computed(() =>
  pageSections.value.some((section) => section.type === 'curated-events'),
);
</script>

<template>
  <div
    class="games select-none pt-[6px] md:pt-0 min-h-[80vh]"
    :style="accentCssVars"
  >
    <!-- Loading global com logo enquanto API carrega seções -->
    <div
      v-if="isLoadingSections && pageSections.length === 0"
      class="flex items-center justify-center min-h-[80vh]"
    >
      <div class="animate-pulse">
        <ApLogoIcon class="w-16 h-16 opacity-60" />
      </div>
    </div>

    <!-- Conteúdo das seções -->
    <Transition
      appear
      name="fade-in"
    >
      <div v-if="pageSections.length > 0">
        <template
          v-for="section in pageSections"
          :key="section.type + Math.random()"
        >
          <PerfilOficialBannerSwiperHome
            v-if="
              section.type === 'banner' &&
              section.data.config.bannerStyle === 'home'
            "
            :accent-color="accentColor"
            :affiliate-details="section.data.affiliateDetails"
            aspect-ratio="141 / 64"
            :banner="section.data.banner"
            :config="section.data.config"
            :desktop-slides-per-view="2"
            :hide-promocode="hasCuratedEventsSection"
            :is-roletas-page="props.isRoletasPage"
            :promocode="section.data.promocode"
          />
          <PerfilOficialBannerSwiper
            v-else-if="section.type === 'banner'"
            :accent-color="accentColor"
            :affiliate-details="section.data.affiliateDetails"
            :banner="section.data.banner"
            :config="section.data.config"
            :is-roletas-page="props.isRoletasPage"
            :promocode="section.data.promocode"
          />

          <SectionsTabsApostatudo v-else-if="section.type === 'tabs'" />
          <PerfilOficialApiTabsGrid
            v-else-if="section.type === 'apiTabs'"
            :is-tab-visible="section.data.isTabVisible"
            :tabs="section.data.tabs"
          />
          <PerfilOficialMissionsCarousel
            v-else-if="section.type === 'missions'"
            :config="section.data.config"
            :is-roletas-page="props.isRoletasPage"
          />
          <PerfilOficialTournamentsCarousel
            v-else-if="section.type === 'tournaments'"
            :accent-color="accentColor"
            :config="section.data.config"
            :is-roletas-page="props.isRoletasPage"
            :smartico-tournament-filter="
              section.data.config.smarticoTournamentFilter
            "
          />
          <PerfilOficialMinigamesCarousel
            v-else-if="section.type === 'minigames'"
            :config="section.data.config"
            icon-path="perfil-oficial/minigames"
            :is-roletas-page="props.isRoletasPage"
            slug="/vip/mini-games"
          />
          <PerfilOficialAlbumCopa
            v-else-if="section.type === 'album'"
            :config="section.data.config"
          />
          <template v-else-if="section.type === 'curated-events'">
            <PerfilOficialHighlightedGamesBar :promocode="props.promocode" />
            <SportsCuratedEventsList hide-header />
          </template>
          <SectionsTabsVIP
            v-else-if="section.type === 'vip'"
            header-icon-path="home/tournament"
            :smartico-minigame-filter="
              section.data.config.smarticoMinigameFilter
            "
            :smartico-mission-filter="section.data.config.smarticoMissionFilter"
            :smartico-store-filter="section.data.config.smarticoStoreFilter"
            :smartico-tournament-filter="
              section.data.config.smarticoTournamentFilter
            "
          />
          <PerfilOficialMediaList
            v-else-if="section.type === 'gameList'"
            :icon-path="section.data.iconPath"
            :is-roletas-page="props.isRoletasPage"
            :items="section.data.items"
            :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
            :see-more-url="section.data.seeMoreUrl"
            :show-numbers="section.data.showNumbers"
            :title="section.data.title"
          />
        </template>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fade-in-enter-active {
  transition: opacity 0.4s ease;
}

.fade-in-enter-from {
  opacity: 0;
}
</style>
